import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FolderKanban, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ProjectSelector - Reusable component for selecting a project
 */
export default function ProjectSelector({
  value,
  onChange,
  allowNone = true,
  disabled = false,
  placeholder = "Select project...",
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchProjects = useCallback(async () => {
    const token = sessionStorage.getItem('auth_token');
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/meeting-intelligence/projects?token=${token}&status=active&limit=50`
      );
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (value && projects.length > 0) {
      const found = projects.find(p => p.id === value);
      setSelectedProject(found || null);
    } else if (!value) {
      setSelectedProject(null);
    }
  }, [value, projects]);

  const handleSelect = (project) => {
    setSelectedProject(project);
    onChange(project?.id || null, project);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedProject(null);
    onChange(null, null);
  };

  const filteredProjects = searchQuery
    ? projects.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.code?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : projects;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between ${className}`}
          disabled={disabled}
          data-testid="project-selector"
        >
          {selectedProject ? (
            <div className="flex items-center gap-2 overflow-hidden">
              <FolderKanban className="h-4 w-4 flex-shrink-0 text-primary" />
              <span className="truncate">{selectedProject.name}</span>
              <Badge variant="outline" className="ml-1 text-xs">
                {selectedProject.code}
              </Badge>
            </div>
          ) : (
            <span className="text-muted-foreground flex items-center gap-2">
              <FolderKanban className="h-4 w-4" />
              {placeholder}
            </span>
          )}
          
          {selectedProject && allowNone ? (
            <X 
              className="h-4 w-4 opacity-50 hover:opacity-100" 
              onClick={handleClear}
            />
          ) : (
            <Search className="h-4 w-4 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Search projects..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Loading...' : 'No projects found'}
            </CommandEmpty>
            
            {allowNone && (
              <CommandGroup heading="General">
                <CommandItem
                  value="none"
                  onSelect={() => handleSelect(null)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <div className="p-1 rounded bg-slate-100 dark:bg-slate-800">
                      <FolderKanban className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium">No Project</p>
                      <p className="text-xs text-muted-foreground">General meeting</p>
                    </div>
                  </div>
                </CommandItem>
              </CommandGroup>
            )}
            
            <CommandGroup heading="Projects">
              {filteredProjects.map(project => (
                <CommandItem
                  key={project.id}
                  value={project.name}
                  onSelect={() => handleSelect(project)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className={`p-1 rounded ${
                      project.status === 'active' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-800'
                    }`}>
                      <FolderKanban className={`h-4 w-4 ${
                        project.status === 'active' ? 'text-green-600' : 'text-slate-500'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{project.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{project.code}</span>
                        <span>•</span>
                        <span>{project.stats?.total_meetings || 0} meetings</span>
                      </div>
                    </div>
                    {value === project.id && (
                      <Badge variant="secondary" className="ml-auto">Selected</Badge>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
